import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import CallRecordingButton from '../CallRecordingButton';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Flame, BookOpen, Sparkles, ArrowRight, UserCheck } from 'lucide-react';
import MyFollowUpsDueCard from './MyFollowUpsDueCard';
import MyRevivableDealsCard from './MyRevivableDealsCard';
import MyAppointmentsCard from './MyAppointmentsCard';
import IntentBreakdownCard from './IntentBreakdownCard';
import MyFollowUpsCard from './MyFollowUpsCard';
import { DateRangePills, formatDaysLabel } from './AnalyticsFilters';
import { CallVolumeHeatmap } from './CallVolumeHeatmap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = '#09D6B4';

const MANAGER_ROLES = new Set([
  'master_admin', 'admin', 'manager', 'supervisor',
  'domain_admin', 'domain_supervisor',
]);

const StatPill = ({ label, value, color = 'bg-slate-100 text-slate-700', title }) => (
  <div className={`rounded-xl px-4 py-3 ${color}`} title={title}>
    <p className="text-xs text-current opacity-70 mb-0.5">{label}</p>
    <p className="text-xl font-bold">{value}</p>
  </div>
);

const IndividualTab = ({ domainId = null }) => {
  const { user, userIndustry } = useAuth();
  const navigate = useNavigate();
  const isAgent = !MANAGER_ROLES.has(user?.role);
  const [agents, setAgents] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [priorityCallbacks, setPriorityCallbacks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [days, setDays] = useState(7); // page-level date filter — every widget follows it
  const daysParam = days == null ? 'all' : String(days);

  useEffect(() => {
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    else if (userIndustry) p.set('call_type', userIndustry);
    const qs = p.toString() ? `?${p}` : '';
    axios.get(`${API}/analytics/team${qs}`, { timeout: 35000 })
      .then(r => {
        const board = r.data.leaderboard || [];
        setAgents(board);
        if (isAgent) {
          // Force self-scope: match by extension or user id
          const mine = board.find((a) =>
            String(a.agent_id) === String(user?.extension || '') ||
            String(a.agent_id) === String(user?.id || ''),
          );
          setSelectedId(mine?.agent_id || board[0]?.agent_id || '');
        } else if (board.length) {
          setSelectedId(board[0].agent_id);
        }
      })
      .catch(() => setAgents([]))
      .finally(() => setLoadingAgents(false));
  }, [userIndustry, domainId, isAgent, user?.extension, user?.id]);

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    axios.get(`${API}/analytics/individual/${selectedId}?days=${encodeURIComponent(daysParam)}`, { timeout: 35000 })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));

    // Priority callbacks — top 3 revivable deals scoped to whichever
    // agent is CURRENTLY SELECTED. Previously omitted the agent scope
    // which meant a manager switching the Select dropdown still saw the
    // logged-in user's revivables. Feb 2026 UX fix.
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    p.set('days', daysParam);
    p.set('limit', '3');
    p.set('sort_by', 'confidence');
    if (selectedId) p.set('agent_id', selectedId);
    axios.get(`${API}/analytics/deal-outcomes/revivable-queue?${p.toString()}`, { timeout: 20000 })
      .then((r) => setPriorityCallbacks(r.data?.queue || []))
      .catch(() => setPriorityCallbacks([]));

    // In-app notifications (manager reassignments etc.). Only relevant
    // for the agent role — managers viewing a rep don't get pinged.
    if (isAgent) {
      axios.get(`${API}/analytics/notifications/mine?unread_only=false&limit=10`, { timeout: 15000 })
        .then((r) => setNotifications(r.data?.notifications || []))
        .catch(() => setNotifications([]));
    }
  }, [selectedId, domainId, isAgent, daysParam]);

  if (loadingAgents) return <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>;
  if (!agents.length) return (
    <div className="py-16 text-center text-slate-500" data-testid="individual-empty-state">
      <p className="text-base font-medium text-slate-600">No agents to show</p>
      <p className="text-sm text-slate-400 mt-1">Individual analytics appear once at least one agent has graded calls.</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="individual-tab">
      {/* Page-level date filter — one control, every widget follows it. */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-white border border-slate-200 rounded-xl px-4 py-2.5" data-testid="individual-date-filter">
        <DateRangePills days={days} onChange={setDays} includeAll={false} />
        <span className="text-[11px] text-slate-400">Every widget on this page reflects {formatDaysLabel(days)}</span>
      </div>
      {/* Agent Selector — hidden for `agent` role users (they only see themselves) */}
      {!isAgent && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-slate-600 whitespace-nowrap">Select Agent:</label>
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="w-56" data-testid="agent-select">
              <SelectValue placeholder="Choose an agent" />
            </SelectTrigger>
            <SelectContent>
              {agents.map(a => (
                <SelectItem key={a.agent_id} value={a.agent_id}>
                  {a.agent_name} — {a.avg_score}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {isAgent && (user?.name || data?.agent?.name) && (
        <div className="rounded-xl bg-gradient-to-br from-teal-50 to-indigo-50 border border-teal-200 px-4 py-3 flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-teal-600" />
          <p className="text-sm text-slate-700">
            <span className="font-semibold">{user?.name || data?.agent?.name}</span> — your personal desk. Everything below is your data only.
          </p>
        </div>
      )}

      {/* Booked Appointments — VERY TOP of the page (Sep-2026 feature):
          any tour / walk-through / meeting the agent booked on a call
          shows immediately, no scrolling. Managers see the selected rep's. */}
      {(isAgent || selectedId) && (
        <MyAppointmentsCard
          domainId={domainId}
          agentId={isAgent ? undefined : selectedId}
          isSelf={isAgent}
        />
      )}

      {/* Coaching Priority Areas — now a compact click-through teaser
          (Sep-2026 spec): the detail lives on the My Coaching tab. */}
      {!loading && data && data.weak_playbooks?.length > 0 && (
        <button
          type="button"
          onClick={() => navigate({ search: '?tab=my-coaching' })}
          className="w-full text-left rounded-xl border border-rose-200 bg-gradient-to-br from-rose-50/40 to-white p-3 flex items-center gap-3 hover:shadow-md transition-shadow"
          data-testid="coaching-priority-teaser"
          title="Open the My Coaching tab for the full coaching breakdown"
        >
          <Sparkles className="w-4 h-4 text-rose-500 shrink-0" />
          <span className="text-sm text-slate-700">
            <span className="font-semibold">Coaching Priority:</span>{' '}
            {data.weak_playbooks[0].skill} ({Math.round(data.weak_playbooks[0].score)}%)
            {data.weak_playbooks.length > 1 && ` +${data.weak_playbooks.length - 1} more`}
          </span>
          <span className="ml-auto text-xs font-semibold text-teal-700 inline-flex items-center gap-1 shrink-0">
            View in My Coaching <ArrowRight className="w-3 h-3" />
          </span>
        </button>
      )}


      {/* Quick-stats row — TOP of the page (Sep-2026 spec) with hover
          tooltips explaining each figure + the active date range. */}
      {!loading && data?.stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="individual-stats-row-top">
          <StatPill label="Avg Score" value={data.stats.avg_score} color="bg-teal-50 text-teal-800" title={`Average AI grading score across all graded calls in the selected range (${formatDaysLabel(days)}).`} />
          <StatPill label="Total Calls" value={data.stats.total_calls} title={`Number of graded calls in the selected range (${formatDaysLabel(days)}).`} />
          <StatPill label="Best Score" value={data.stats.best_score} color="bg-emerald-50 text-emerald-800" title={`Highest single-call grading score in the selected range (${formatDaysLabel(days)}).`} />
          <StatPill label="Worst Score" value={data.stats.worst_score} color="bg-rose-50 text-rose-800" title={`Lowest single-call grading score in the selected range (${formatDaysLabel(days)}).`} />
          <StatPill label="Top Skill" value={data.stats.top_skill?.split(' ').slice(0, 2).join(' ') || '—'} color="bg-violet-50 text-violet-800" title={`The coaching area demonstrated most consistently across graded calls in the selected range (${formatDaysLabel(days)}).`} />
          <StatPill label="Needs Work" value={data.stats.weakest_skill?.split(' ').slice(0, 2).join(' ') || '—'} color="bg-amber-50 text-amber-800" title={`The coaching area with the lowest met-rate across graded calls in the selected range (${formatDaysLabel(days)}).`} />
        </div>
      )}

      {/* My 2-Week Follow-ups — self-view only (Sep-2026 spec) */}
      {isAgent && <MyFollowUpsCard />}

      {/* "My Follow-Ups Due Today" — first-thing-in-the-morning list.
          Only rendered for agents; supervisors/master admins looking at
          another agent via masquerade also see it (same endpoint,
          agent-scoped by RBAC). */}
      {isAgent && <MyFollowUpsDueCard domainId={domainId} />}

      {/* Priority Callbacks Today — self-view only (Sep-2026 spec) */}
      {isAgent && priorityCallbacks.length > 0 && (
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50/40 to-white shadow-sm" data-testid="priority-callbacks-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Flame className="w-4 h-4 text-amber-500" />
              Priority Callbacks Today
              <span className="ml-auto text-[11px] font-medium text-slate-500">
                Ranked by engine confidence
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="space-y-2">
              {priorityCallbacks.map((cb) => (
                <li
                  key={cb.call_id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:border-teal-300 transition-colors"
                  data-testid={`priority-callback-${cb.call_id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm shrink-0">
                    {cb.confidence_score || '—'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-mono text-xs text-slate-800 font-semibold">
                        {cb.customer_phone || '—'}
                      </p>
                      {cb.caller_disc && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                          DISC · {cb.caller_disc}
                        </span>
                      )}
                      {cb.reassigned_by_manager && (
                        <span
                          className="text-[10px] font-semibold uppercase tracking-wider text-purple-700 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5"
                          title={cb.manager_note || 'Allocated by your manager'}
                        >
                          Manager allocated
                        </span>
                      )}
                      {cb.age_days != null && (
                        <span className="text-[10px] text-slate-500">
                          {cb.age_days === 0 ? 'today' : `${cb.age_days}d old`}
                        </span>
                      )}
                    </div>
                    {cb.manager_note && (
                      <p className="text-[11px] text-purple-700 italic mt-0.5">
                        &ldquo;{cb.manager_note}&rdquo;
                      </p>
                    )}
                    <p className="text-xs text-slate-600 mt-0.5 line-clamp-1">
                      {cb.reason_detected || cb.recommended_next_action}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <CallRecordingButton callId={cb.call_id} size="xs" />
                    <a
                      href={`/calls/${cb.call_id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-lg bg-gradient-to-br from-teal-600 to-teal-700 hover:from-teal-500 hover:to-teal-600 text-white shadow-sm"
                      data-testid={`priority-open-${cb.call_id}`}
                    >
                      <BookOpen className="w-3 h-3" /> Playbook
                      <ArrowRight className="w-3 h-3" />
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Your Revivable Deals — ABOVE the heatmap (Sep-2026 bug fix:
          Darren wants Priority Callbacks → Revivable Deals → Heatmap). */}
      {(isAgent || selectedId) && (
        <MyRevivableDealsCard
          domainId={domainId}
          agentId={isAgent ? user?.extension : selectedId}
          isSupervisor={!isAgent}
          days={typeof days === 'number' ? days : 30}
        />
      )}

      {/* Call Volume Heatmap — moved here from Intelligence (Sep-2026
          spec). Self-view only; follows the page-level date filter. */}
      {isAgent && (
        <CallVolumeHeatmap domainId={domainId} forcedAgentId={selectedId} daysOverride={days} />
      )}

      {isAgent && notifications.length > 0 && (() => {
        const unread = notifications.filter((n) => !n.read_at);
        if (unread.length === 0) return null;
        const dismiss = async () => {
          try {
            await axios.post(`${API}/analytics/notifications/mark-read?all_flag=true`);
            setNotifications((prev) => prev.map((n) => ({ ...n, read_at: new Date().toISOString() })));
          } catch { /* silent */ }
        };
        return (
          <Card className="border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50 shadow-sm" data-testid="reassign-notifications-card">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold shrink-0">
                  {unread.length}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-purple-900">
                    You have {unread.length} new manager-allocated {unread.length === 1 ? 'deal' : 'deals'}
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {unread.slice(0, 3).map((n) => (
                      <li
                        key={n.call_id}
                        className="text-xs text-slate-700 bg-white/70 rounded-lg px-3 py-2 border border-purple-100"
                        data-testid={`reassign-notice-${n.call_id}`}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-semibold">{n.customer_phone || '—'}</span>
                          {n.caller_disc && (
                            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                              DISC · {n.caller_disc}
                            </span>
                          )}
                          <span className="text-[10px] text-slate-400 ml-auto">
                            from {n.from_manager_email?.split('@')[0] || 'manager'}
                          </span>
                        </div>
                        {n.note && (
                          <p className="mt-1 text-[11px] text-purple-800 italic">&ldquo;{n.note}&rdquo;</p>
                        )}
                        <a
                          href={`/calls/${n.call_id}`}
                          className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold text-teal-700 hover:text-teal-900"
                        >
                          Open call <ArrowRight className="w-2.5 h-2.5" />
                        </a>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={dismiss}
                    className="mt-2 text-[11px] font-semibold text-purple-700 hover:text-purple-900"
                    data-testid="dismiss-reassign-notices"
                  >
                    Got it — mark all as read
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {loading && <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>}

      {!loading && data && (
        <>
          {/* Score Trend + Skill Breakdown moved to My Coaching (Sep-2026 spec). */}
          <IntentBreakdownCard
            agentId={selectedId}
            domainId={domainId}
            days={daysParam}
          />

          {/* Recent Calls */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base">Recent Calls</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Date</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Call Type</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Score</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Status</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Listen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_calls.map((call, i) => {
                      // Clicking anywhere on the row navigates to the call
                      // detail page — agents on their /my-domain dashboard
                      // asked to jump straight into the transcript instead
                      // of only being able to open the audio player.
                      const rowContent = (
                        <>
                          <td className="py-2 px-3 text-slate-600">{call.date}</td>
                          <td className="py-2 px-3 text-slate-700">{call.call_type}</td>
                          <td className="py-2 px-3">
                            <span className={`font-semibold ${call.score >= 80 ? 'text-emerald-600' : call.score >= 65 ? 'text-amber-600' : 'text-rose-600'}`}>
                              {call.score}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${call.status === 'escalated' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                              {call.status}
                            </span>
                          </td>
                          <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                            {call.call_id ? (
                              <CallRecordingButton callId={call.call_id} size="xs" />
                            ) : (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                          </td>
                        </>
                      );
                      if (call.call_id) {
                        return (
                          <tr
                            key={call.call_id || `report-row-${i}`}
                            className="border-b border-slate-50 hover:bg-teal-50 cursor-pointer transition-colors"
                            onClick={() => navigate(`/calls/${call.call_id}`)}
                            data-testid={`recent-call-row-${call.call_id}`}
                            title="Open call detail"
                          >
                            {rowContent}
                          </tr>
                        );
                      }
                      return (
                        <tr key={`report-row-${i}`} className="border-b border-slate-50 hover:bg-slate-50">
                          {rowContent}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default IndividualTab;
